export type ContractType =
  | 'works'
  | 'supply'
  /** Разовая купля-продажа (техника, оборудование) — не то же, что поставка партиями. */
  | 'purchase'
  | 'services'
  | 'lease'
  | 'agency'
  | 'other';

/** Тип мягко удалённой сущности юр-агента. Все статут-сущности
 *  (документы БЗ, договоры, претензии, иски, ЛНА) собираются в единую
 *  «Корзину» с группировкой по типу. Дискриминант в {@link TrashItem}. */
export type TrashEntityType = 'knowledge' | 'contract' | 'claim' | 'lawsuit' | 'lna';

/** Человекочитаемые названия типов для «Корзины». */
export const TRASH_TYPE_LABELS: Record<TrashEntityType, string> = {
  knowledge: 'Документ базы знаний',
  contract: 'Договор',
  claim: 'Претензия',
  lawsuit: 'Иск',
  lna: 'ЛНА',
};

/** Элемент «Корзины»: мягко удалённая запись любого статут-типа.
 *  Форма едина для всех типов — `entity_type` служит дискриминантом. */
export interface TrashItem {
  entity_type: TrashEntityType;
  entity_id: string;
  title: string;
  subtitle: string | null;
  deleted_at: string;
  deleted_by: string | null;
}

/** Счётчик записей в «Корзине» по одному типу (для чипов-фильтров). */
export interface TrashCount {
  entity_type: TrashEntityType;
  count: number;
}

/** запись audit-журнала. */
export interface AuditEntry {
  id: number;
  user_id: string | null;
  user_email?: string | null;
  action: string;
  resource: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

/** статусы контрагента из ЕГРЮЛ. */
export type CounterpartyStatus =
  | 'ACTIVE'
  | 'LIQUIDATING'
  | 'LIQUIDATED'
  | 'REORGANIZING'
  | 'BANKRUPT'
  | 'UNKNOWN';

export interface Counterparty {
  id: string;
  inn: string;
  kpp: string | null;
  ogrn: string | null;
  full_name: string;
  short_name: string | null;
  /** LEGAL | INDIVIDUAL */
  party_type: string;
  legal_form: string | null;
  address: string | null;
  director_name: string | null;
  director_position: string | null;
  registration_date: string | null;
  liquidation_date: string | null;
  status: CounterpartyStatus | string;
  egrul_data: Record<string, unknown>;
  last_checked_at: string | null;
  /** Свободный JSON-набор флагов риска (rnp/fssp и пр.). Блок
   *  `requisite_changes` фронт использует для быстрой отметки расхождений
   *  реквизитов на карточке без отдельного запроса — детали тянутся
   *  {@link RequisiteCheck} по кнопке. Остальные ключи не типизированы. */
  risk_flags: Record<string, unknown> & { requisite_changes?: RequisiteChanges };
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CounterpartyDetails {
  counterparty: Counterparty;
  contracts_count: number;
  claims_count: number;
  /** Число исков (lawsuits), связанных с контрагентом. Считается в Postgres
   *  вместе с contracts_count/claims_count — фронт получает готовое число. */
  lawsuits_count: number;
  egrul_enabled: boolean;
}

/** snapshot проверки в Реестре недобросовестных поставщиков. */
export interface RnpFlag {
  inn?: string;
  checked_at?: string;
  is_listed?: boolean;
  records_count?: number;
  source_url?: string;
  /** Заполняется если запрос упал — отображаем юристу как warning. */
  error?: string;
}

/** snapshot проверки в ФССП (исполнительные производства). */
export interface FsspFlag {
  inn?: string;
  checked_at?: string;
  open_executions?: number;
  total_debt_rub?: number | null;
  /** real | unavailable */
  source?: string;
  error?: string;
}

// Локальный нормативный акт
export interface LnaRecord {
  id: string;
  title: string;
  lna_type: string;
  effective_from: string | null;
  expires_at: string | null;
  status: string;
  file_hash: string | null;
  owner_department: string | null;
  description: string | null;
  content_text: string | null;
  visible_in_assistant?: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Запрос от госоргана
export interface GovRequest {
  id: string;
  authority: string;
  request_no: string | null;
  subject: string;
  content_text: string | null;
  received_at: string;
  deadline: string | null;
  status: string;
  priority: string;
  assigned_lawyer_id: string | null;
  is_inspection: boolean;
  response_text: string | null;
  response_sent_at: string | null;
  legality_check: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** 'auto' — создано из письма классификатором; 'manual' — введено вручную. */
  source: 'auto' | 'manual';
}

// Событие мониторинга НПА
export interface NpaEvent {
  id: string;
  source: string;
  event_type: string;
  title: string;
  summary: string | null;
  url: string | null;
  occurred_at: string;
  fetched_at: string;
  severity: string;
  tags: string[];
  notified: boolean;
  raw: Record<string, unknown>;
  created_at: string;
}

export interface NpaDigestCounts {
  total: number;
  critical: number;
  warning: number;
  info: number;
  laws: number;
  court_practice: number;
  reviews: number;
}

/** Агрегированный snapshot для главной страницы. Все counts посчитаны
 *  в Postgres одним SQL — фронт получает готовые числа без локального
 *  filter().length. См. backend/crates/osnova-storage/src/dashboard.rs */
export interface DashboardSnapshot {
  contracts_total: number;
  contracts_under_review: number;
  contracts_analyzed: number;

  /** Незаключённые договоры на проработке (реестр «Преддоговорная работа»).
   *  Считается тем же SQL, что и contracts_total, но с record_kind='precontract'. */
  precontracts_total: number;

  claims_total: number;
  claims_active: number;
  claims_lawsuits: number;

  correspondence_total: number;
  correspondence_unclassified: number;
  correspondence_returned: number;
  correspondence_classified: number;

  counterparties_total: number;
  counterparties_liquidated: number;
  counterparties_at_risk: number;

  deadlines_total: number;
  deadlines_overdue: number;
  deadlines_within_7: number;
  deadlines_next_due_at: string | null;

  checks_total: number;
  checks_active: number;

  chat_sessions_total: number;
  chat_last_at: string | null;

  contract_templates_total: number;
  contract_templates_active: number;

  knowledge_total: number;
  knowledge_tech: number;
  knowledge_legal: number;

  lna_total: number;
  lna_active: number;
  lna_expired: number;

  gov_total: number;
  gov_in_work: number;
  gov_overdue: number;
}

/** сводные метрики качества работы юр.агента. */
/** Один вид запусков резервного копирования (журнал ops_backup_runs, Б-1). */
export interface BackupKindSummary {
  last_status: 'ok' | 'failed' | null;
  last_at: string | null;
  last_message: string | null;
  last_ok_at: string | null;
  last_ok_set: string | null;
  /** Часов с последней удачи. */
  age_hours: number | null;
  /** details последнего удачного запуска: размеры, счётчики ключевых таблиц. */
  last_ok_details: Record<string, unknown> | null;
}

/** Состояние резервного копирования базы — блок `backups` в метриках. */
export interface BackupHealth {
  /** unknown — журнал не прочитан (остальных полей тогда нет). */
  state: 'ok' | 'failed' | 'not_configured' | 'unknown';
  problems: Array<{ code: string; text: string }>;
  dump?: BackupKindSummary;
  offsite?: BackupKindSummary;
  restore_test?: BackupKindSummary;
  /** s3 — копия выносится каждым запуском; laptop — офсайт-копией администратора. */
  offsite_mode?: 's3' | 'laptop';
  dump_max_age_hours?: number;
  offsite_max_age_hours?: number;
  restore_max_age_days?: number;
  evaluated_at?: string;
}

export interface StatutMetrics {
  /** Резервное копирование базы (Б-1). Нет у старого backend'а. */
  backups?: BackupHealth;
  contracts: {
    total: number;
    under_review: number;
    analyzed: number;
    risks_total: number;
    risks_annotated: number;
    risks_accepted: number;
    risks_rejected: number;
    risks_disputed: number;
    acceptance_rate_pct: number;
    target_acceptance_pct: number;
  };
  law_refs: {
    total: number;
    verified: number;
    accuracy_pct: number;
    target_accuracy_pct: number;
  };
  correspondence: {
    total: number;
    classified: number;
    returned: number;
    classification_rate_pct: number;
    reroute_rate_pct: number;
    target_classification_pct: number;
    target_reroute_max_pct: number;
  };
  deadlines: {
    total: number;
    pending: number;
    overdue: number;
    notified: number;
    target_overdue_max: number;
  };
  counterparties: {
    total: number;
    active: number;
    at_risk: number;
    liquidated_or_bankrupt: number;
  };
  knowledge_base: {
    total: number;
    codes_fz_plenum: number;
    tech_norms: number;
    contract_templates: number;
  };
  /** HITL-разметка по претензиям/искам (зеркало contracts). */
  claims: {
    risks_total: number;
    risks_annotated: number;
    risks_accepted: number;
    risks_rejected: number;
    risks_disputed: number;
    acceptance_rate_pct: number;
  };
  /** «Где LLM пропускает» — за 7 дней и всего. */
  hitl_quality: {
    manual_contract_risks_7d: number;
    manual_claim_risks_7d: number;
    manual_contract_risks_total: number;
    manual_claim_risks_total: number;
    lawyer_corrected_contract_total: number;
    lawyer_corrected_claim_total: number;
    pending_review_contracts: number;
    pending_review_claims: number;
  };
  /** Распределение critical/major/minor (договоры + претензии). */
  severity_distribution: {
    critical: number;
    major: number;
    minor: number;
  };
  /** Топ-5 категорий договоров по числу рисков. */
  top_categories: Array<{ category: string; total: number; critical: number }>;
}

export interface Contract {
  id: string;
  counterparty: string;
  /** FK на counterparties; null — старая запись или контрагент не указан. */
  counterparty_id: string | null;
  /** Вид записи реестра: `contract` — заключённый (основной реестр «Договоры»);
   *  `precontract` — незаключённый, на проработке (реестр «Преддоговорная
   *  работа»). Карточка/анализ/риски общие — различается только реестр и
   *  переход «Договор заключён» (PATCH /contracts/:id/conclude). Строка (а не
   *  строгий union) — на случай новых видов с backend, без падения фронта. */
  record_kind: 'contract' | 'precontract' | string;
  /** Для преддоговора в статусе `concluded`: id созданного заключением договора
   *  (чистая карточка для подписанного скана). Для фронта — бейдж-ссылка
   *  «Заключён → перейти в Договоры». Опционально (нет в старом кэше). */
  concluded_contract_id?: string | null;
  /** Для договора, созданного заключением преддоговора: id преддоговора-
   *  источника. Опционально. */
  originated_from_precontract_id?: string | null;
  /** Момент попадания в архив преддоговорной работы (миграция 0210). Заполнено
   *  только у преддоговоров: заключённый архивируется при conclude, брошенный —
   *  суточным уборщиком по 30 дням тишины. null/отсутствие — в рабочем реестре. */
  archived_at?: string | null;
  /** Номер договора. LLM извлекает при анализе; юрист может задать/исправить
   *  вручную (PUT /contracts/:id/number). null — номер не определён. */
  contract_number?: string | null;
  subject: string | null;
  sum: number | null;
  currency: string | null;
  signed_at: string | null;
  expires_at: string | null;
  status: string;
  contract_type: ContractType | string | null;
  responsible_user_id: string | null;
  original_file_hash: string | null;
  /** Свободная заметка юриста (миграция 0211): произвольный текст о состоянии
   *  дела — где оригиналы, статус подписания и т.п. Ручной текст, LLM не трогает.
   *  null/отсутствие — заметки нет. Редактируется POST /contracts/:id/notes. */
  note?: string | null;
  project_id?: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface ContractTemplate {
  id: string;
  contract_type: ContractType | string;
  name: string;
  content_text: string;
  file_hash: string | null;
  description: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Если есть — шаблон проиндексирован в RAG (knowledge_chunks с embeddings). */
  knowledge_document_id: string | null;
}

export interface TemplateDeviation {
  clause: string;
  description: string;
  template_says: string | null;
  severity: string | null;
}

export type ClaimType = 'claim' | 'lawsuit';

/** Направление претензии/иска — определяет ветку LLM-анализа.
 *  `incoming`: пришло от контрагента/суда → защитный анализ
 *  `outgoing`: наш проект → самопроверка перед отправкой */
export type ClaimDirection = 'incoming' | 'outgoing';

/** Статус LLM-анализа загруженного файла претензии. */
export type ClaimAnalysisStatus = 'pending' | 'processing' | 'analyzed' | 'failed';

export interface Claim {
  id: string;
  claim_type: ClaimType | string;
  counterparty: string;
  counterparty_id?: string | null;
  dispute_type: string | null;
  contract_id: string | null;
  project_id?: string | null;
  stage: string;
  assigned_lawyer_id: string | null;
  forecast: string | null;
  amount: number | null;
  created_at: string;
  direction: ClaimDirection | string;
  original_file_hash: string | null;
  analysis_status: ClaimAnalysisStatus | string;
  analyzed_at: string | null;
  summary: string | null;
  /** JSON-метаданные анализа: контррасчёт, прогноз, нормы, draft_response для incoming. */
  analysis_meta: Record<string, unknown>;
}

/** Distilled finding из аудита внешнего ИИ — для UI подтверждения юристом
 *  перед сохранением в слой памяти агента. Verdict совпадает с CHECK
 *  constraint'ом БД (см. миграцию 0025). */
export type AuditVerdict =
  | 'agent_missed'
  | 'agent_wrong'
  | 'agent_overestimate'
  | 'agent_underestimate'
  | 'agent_correct';

/** Async-сессия парсинга внешнего аудита. Создаётся при POST /audit/parse,
 *  worker обрабатывает в фоне, фронт подписывается на изменения через WS
 *  (audit_session_updated). При закрытии модалки сессия продолжает жить. */
export type AuditSessionStatus = 'parsing' | 'ready' | 'confirmed' | 'failed';

/** Над чем проводится внешний аудит. Договор и претензия имеют собственные
 *  REST-префиксы (`/contracts/{id}`, `/claims/{id}`); все четыре типа
 *  сгенерированных DOCX (протокол разногласий, ответ на претензию,
 *  перевыставление субподрядчику, иск) аудируются через общий
 *  `/composed/{id}` — у них одна таблица composed_responses, scope отличает
 *  лишь source_template_kind исходного шаблона. */
export type AuditScope =
  | 'contract'
  | 'claim'
  | 'protocol_disagreement'
  | 'response_claim'
  | 'outgoing_claim'
  | 'lawsuit';

/** Режим аудита сгенерированного документа:
 *  - `audit`     — внешний ИИ прислал именно аудит (замечания к нашему DOCX);
 *  - `reference` — внешний ИИ составил эталонный документ по той же сделке,
 *    парсер сравнивает его с нашим и выдаёт расхождения как findings.
 *  У старых сессий поле может отсутствовать → трактуем как 'audit'. */
export type AuditSessionKind = 'audit' | 'reference';

export interface AuditSession {
  id: string;
  scope: AuditScope | string;
  entity_id: string;
  source_model: string;
  raw_text: string;
  status: AuditSessionStatus | string;
  /** 'audit' (текст аудита) | 'reference' (эталонный документ). У сессий,
   *  созданных до появления режима reference, поле отсутствует — считать
   *  'audit'. */
  kind?: AuditSessionKind;
  parsed_findings: AuditFinding[] | null;
  parsed_summary: string | null;
  error_message: string | null;
  confirmed_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  confirmed_at: string | null;
}

export interface AuditFinding {
  verdict: AuditVerdict | string;
  finding: string;
  corrected_assessment?: string | null;
  related_risk_id?: string | null;
  corrected_severity?: 'critical' | 'major' | 'minor' | null;
  corrected_category?: string | null;
  tags?: string[];
}

export interface ClaimRisk {
  id: string;
  claim_id: string;
  category: string;
  severity: string;
  description: string;
  proposed_action: string | null;
  confidence: number | null;
  refs: ValidatedRef[];
  created_at: string;
  /** HITL: решение юриста по риску. null = ещё не размечен. */
  decision?: RiskDecision | null;
  decision_comment?: string | null;
  decision_by?: string | null;
  decision_at?: string | null;
  /** Источник: 'llm' (LLM-сгенерировано), 'manual' (юрист добавил вручную),
   *  'lawyer_corrected' (LLM-сгенерировано, юрист правил текст). */
  source?: 'llm' | 'manual' | 'lawyer_corrected';
  created_by?: string | null;
  /** Дословная цитата проблемного фрагмента документа (LLM-extract).
   *  Используется в side-panel «исходный текст пункта». */
  clause_quote?: string | null;
  /** «Почему агент так решил» — JSONB-объект с массивами цитат прецедентов,
   *  применённых норм и пунктов типовой формы. См. миграцию 0043. */
  evidence_used?: EvidenceUsed | null;
}

export interface EvidenceUsed {
  precedent_quotes?: string[];
  law_articles?: string[];
  template_clauses?: string[];
}

export type CorrespondenceSource = 'dispatcher' | 'internal' | 'manual';

/** Статус одной цели мульти-адресной пересылки письма (fan-out).
 *  Зеркало backend `mailbox_forward_targets.status` (CHECK-constraint,
 *  миграция 0099). `skipped` — адрес уже в копии письма (анти-петля),
 *  SMTP не дёргался; `manual` — отправлено вручную юристом. */
export type DispatcherForwardTargetStatus =
  | 'pending'
  | 'sent'
  | 'failed'
  | 'skipped'
  | 'manual';

/** Одна цель fan-out пересылки. Шапка (`DispatcherForward`) остаётся одна
 *  на письмо; цели — one-to-many (миграция 0099). */
export interface DispatcherForwardTarget {
  id: string;
  route_id?: string | null;
  email: string;
  status: DispatcherForwardTargetStatus | string;
  confidence?: number | null;
  sent_message_id?: string | null;
  last_error?: string | null;
  skip_reason?: string | null;
  created_at: string;
  updated_at: string;
}

/** Категория письма для авто-разворота в ящики отделов (Вариант 3, гибрид).
 *  LLM классифицирует письмо в одну категорию, затем детерминированный
 *  маппинг `category → routes` (редактируется в админке) разворачивает её
 *  в набор ящиков. Зеркало backend `mailbox_categories` (миграция 0100).
 *  `route_ids` — текущий маппинг на ящики; пустой = категория не разводится. */
export interface MailboxCategory {
  id: string;
  code: string;
  display_name: string;
  /** Кладётся в промпт LLM при классификации — описывает, какие письма
   *  относятся к категории. */
  description: string;
  is_active: boolean;
  route_ids: string[];
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

/** Входной DTO создания/редактирования категории (зеркало backend
 *  `MailboxCategoryInput`). `code` — `[A-Za-z0-9_-]`, неизменяемый якорь
 *  для маппинга. */
export interface MailboxCategoryInput {
  code: string;
  display_name: string;
  description: string;
  is_active?: boolean;
}

/** Один кандидат-договор при разрешении ссылки на договор из письма
 *  (status==='ambiguous'). Обогащён предметом/датой/контрагентом, чтобы
 *  юрист мог выбрать нужный без второго запроса. */
export interface ContractLinkCandidate {
  contract_id: string;
  contract_number: string | null;
  subject: string | null;
  signed_at: string | null;
  counterparty: string | null;
}

/** Результат авто-разрешения договора по номеру, извлечённому из письма.
 *  Кладётся LLM-классификатором в `agent_classification.contract_link`.
 *  - `linked`     — номер однозначно сматчился, см. `contract_id`.
 *  - `ambiguous`  — несколько договоров с этим номером, см. `candidates` (HITL-выбор).
 *  - `not_found`  — номер извлечён, но договора нет (или номер не извлечён). */
export interface ContractLink {
  status: 'linked' | 'ambiguous' | 'not_found';
  number: string | null;
  /** Заполняется при status==='linked'. */
  contract_id?: string;
  /** Заполняется при status==='ambiguous'. */
  candidates?: ContractLinkCandidate[];
}

/** `agent_classification` письма — свободный JSON от LLM. Большинство полей
 *  не типизировано (рендерится как pre), но блок `contract_link` фронт
 *  использует для секции «Договор» в карточке письма. */
export type CorrespondenceClassification = Record<string, unknown> & {
  contract_link?: ContractLink;
};

export interface Correspondence {
  id: string;
  source: CorrespondenceSource | string;
  incoming_at: string;
  sender: string | null;
  subject: string | null;
  content_text: string | null;
  original_file_hash: string | null;
  dispatcher_classification: Record<string, unknown> | null;
  agent_classification: CorrespondenceClassification | null;
  /** FK на договор, к которому привязано письмо. null — не привязано.
   *  Ставится автоматически (status==='linked') или вручную юристом
   *  (PUT /correspondence/:id/contract). */
  contract_id?: string | null;
  project_id?: string | null;
  status: string;
  returned_to_dispatcher: boolean;
  assigned_user_id: string | null;
  draft_response: string | null;
  draft_generated_at: string | null;
  /** 'incoming' (default) | 'outgoing'. */
  direction?: string;
  /** Заголовок To письма (для исходящих — куда отправили). */
  recipients?: string[];
  /** Cc — копии. */
  cc?: string[];
  /** Ящик-источник, из которого диспетчер забрал письмо (label аккаунта,
   *  напр. адрес ящика). null/undefined — legacy-запись или единственный
   *  ящик (в UI показывается как «Основной»). По этому полю фильтруется и
   *  агрегируется переключатель ящиков в inbox диспетчера. */
  source_mailbox?: string;
}

export type DeadlineScope = 'contract' | 'claim' | 'correspondence' | 'gov';

export interface Deadline {
  id: string;
  scope: DeadlineScope | string;
  entity_id: string;
  due_at: string;
  kind: string;
  notified_at: string | null;
  status: string;
  note: string | null;
  /** Название контрагента/отправителя для отображения в реестре. */
  entity_title?: string | null;
  /** Предмет договора / тип спора / тема письма. */
  entity_subtitle?: string | null;
  /** Для претензий 'claim'|'lawsuit'; для остальных scope — null. */
  entity_kind?: string | null;
  /** Готовая ссылка на карточку документа (`/statut/contracts/{id}` и т.п.). */
  entity_url?: string | null;
}

/** Условие исполнения договора (ТЗ: «порядок исполнения», миграция 0206).
 *  Условное обязательство с триггером — «аванс 30% через 5 дней после подписания».
 *  В отличие от Deadline, даты часто нет до наступления триггера. */
export interface ExecutionTerm {
  id: string;
  contract_id: string;
  /** Вид: payment_stage | delivery | quality_acceptance | work_milestone |
   *  notification_period | other. */
  kind: string;
  /** Человекочитаемое условие: «Аванс 30% после подписания договора». */
  title: string;
  /** Событие-триггер: contract_signed | document_date | act_signed |
   *  invoice_issued | delivery | acceptance | other. */
  trigger_event: string | null;
  duration_days: number | null;
  /** ISO-дата если в договоре прямо указана (редко для условий). */
  due_date: string | null;
  /** Ответственный отдел: accounting | mto | pto | field | legal | tender | other. */
  responsible_role: string | null;
  /** Пункт договора: «п. 7.3». */
  clause: string | null;
  note: string | null;
  sort_order: number;
  created_at: string;
}

export interface ContractVersion {
  id: string;
  contract_id: string;
  version_no: number;
  file_hash: string;
  uploaded_by: string | null;
  uploaded_at: string;
  source: 'counterparty' | 'company' | string;
}

export type RiskDecision = 'accepted' | 'disputed' | 'rejected';

/** одна валидированная ссылка на НПА. */
export interface ValidatedRef {
  citation: string;
  /** `code` | `fz` | `plenum` */
  norm_type: string;
  found: boolean;
  source_doc_id?: string | null;
  source_title?: string | null;
  score: number;
}

export interface ContractRisk {
  id: string;
  contract_id: string;
  version_id: string | null;
  category: string;
  severity: string;
  description: string;
  confidence: number | null;
  proposed_change: string | null;
  /** HITL — решение юриста по риску. null = ещё не размечен. */
  decision: RiskDecision | string | null;
  decision_comment: string | null;
  decision_at: string | null;
  /** список проверенных ссылок. Пустой массив = LLM не цитировала
   * ничего, либо валидация не прошла. */
  refs: ValidatedRef[];
  /** Источник: 'llm' (LLM-сгенерировано), 'manual' (юрист добавил),
   *  'lawyer_corrected' (LLM-сгенерировано, юрист правил текст). */
  source?: 'llm' | 'manual' | 'lawyer_corrected';
  created_by?: string | null;
  /** Дословная цитата проблемного фрагмента (LLM-extract). См. ClaimRisk.clause_quote. */
  clause_quote?: string | null;
  /** «Почему агент так решил». См. ClaimRisk.evidence_used. */
  evidence_used?: EvidenceUsed | null;
  /** Файл комплекта, в котором найден риск. null/undefined — одиночный файл,
   * версия до появления комплектов либо риск, добавленный юристом руками. */
  source_document?: string | null;
}

export interface ContractDetails {
  contract: Contract;
  versions: ContractVersion[];
  risks: ContractRisk[];
  claims: Claim[];
  deadlines: Deadline[];
  execution_terms: ExecutionTerm[];
}

export interface ContractDiffItem {
  clause: string;
  change_type: 'added' | 'removed' | 'modified' | string;
  from_text: string | null;
  to_text: string | null;
  summary: string | null;
  favorable_for_satkom: boolean | null;
}

export interface ContractVersionDiff {
  from_version_no: number;
  to_version_no: number;
  items: ContractDiffItem[];
  summary: string | null;
  analyzed_clauses: number;
}

export interface ClaimDetails {
  claim: Claim;
  contract: Contract | null;
  deadlines: Deadline[];
  risks: ClaimRisk[];
  /** Счётчики HITL-рисков связанного договора, которые LLM использовала
   *  при анализе этой претензии. null = нет связанного договора. */
  related_contract_context?: {
    contract_id: string;
    total: number;
    accepted: number;
    rejected: number;
    disputed: number;
    pending: number;
  } | null;
  /** Направление, похоже, перепутано: какое стоит поставить. Документ на
   *  бланке САТКОМ заведён входящим (или САТКОМ — заявитель) → 'outgoing';
   *  исходящий, где САТКОМ — ответчик → 'incoming'. null — всё сходится. */
  direction_hint?: ClaimDirection | null;
}

export interface CorrespondenceAttachment {
  id: string;
  correspondence_id: string;
  filename: string;
  content_type: string | null;
  file_hash: string;
  size_bytes: number;
  created_at: string;
}

export interface CorrespondenceDetails {
  correspondence: Correspondence;
  deadlines: Deadline[];
  /** Если письмо классифицировано как gov_request — связанная запись в реестре «Госорганы». */
  linked_gov_request: GovRequest | null;
  /** Если письмо помечено как pretension/court_doc/bankruptcy — связанная запись в реестре «Претензии». */
  linked_claim: Claim | null;
  /** Если письмо помечено как contract_draft — связанная запись в реестре «Договоры». */
  linked_contract: Contract | null;
  /** Файлы, пришедшие вместе с письмом (DOCX/PDF/...). */
  attachments: CorrespondenceAttachment[];
}

export interface LnaRecord {
  id: string;
  title: string;
  lna_type: string;
  effective_from: string | null;
  expires_at: string | null;
  status: string;
  file_hash: string | null;
  owner_department: string | null;
  created_at: string;
}

/** Файл из комплекта конкретной версии договора. Один договор = N версий,
 *  одна версия = N файлов (основной + приложения + спецификации). */
export interface ContractVersionFile {
  id: string;
  version_id: string;
  filename: string;
  content_type: string | null;
  file_hash: string;
  size_bytes: number;
  ordinal: number;
  created_at: string;
  /** Сколько символов текста реально извлечено и попало в LLM-анализ.
   *  0 = файл не распарсился (скан/битый PDF/image-only) — в анализ НЕ
   *  участвовал. Юрист видит «0 символов» и понимает, что приложение
   *  пропущено. Поле опционально на случай старых записей. */
  content_chars?: number;
  /** Тип документа (`DocumentKind`, миграция 0208). Решает, попадёт ли файл
   *  в промпт юр-агента: в анализ идут только contract /
   *  protocol_of_disagreement / requisites. null — версия загружена до
   *  классификации либо тип ещё не определён. */
  document_kind?: string | null;
  /** Кто определил тип: pending | heuristic | llm | lawyer. Ручная правка
   *  юриста (`lawyer`) сильнее автоматики и переживает перепарс комплекта. */
  kind_source?: string;
  /** «Приложен без анализа» (миграция 0212): файл приложен галочкой «без
   *  переанализа» — сохранён и скачивается, но НЕ подаётся в LLM (ни сейчас,
   *  ни при ре-ране). Для таких файлов «0 символов» — норма, а не ошибка. */
  no_analysis?: boolean;
  routed_agent?: string | null;
  routed_entity_id?: string | null;
  routed_at?: string | null;
}

/** In-app уведомление сотрудника отдела о присланных ему рисках договора
 *  (рассылка по отделам, миграция 0213). `target_id` — id документа отдела;
 *  по нему идёт скачивание и отметка «показано/скачано». */
export interface RiskNotification {
  id: string;
  target_id: string;
  contract_id: string;
  counterparty: string;
  subject: string | null;
  department: string | null;
  focus_count: number;
  risk_count: number;
  filename: string;
  seen_at: string | null;
  downloaded_at: string | null;
  created_at: string;
  response_status?: string;
  response_comment?: string | null;
  responded_at?: string | null;
}

export interface RiskDispatchTarget {
  id: string;
  dispatch_id: string;
  route_id: string | null;
  department: string | null;
  email: string;
  notify_role: string | null;
  filename: string;
  focus_count: number;
  email_status: string;
  email_message_id: string | null;
  email_error: string | null;
  created_at: string;
  service_id?: string | null;
  response_status: string;
  response_comment: string | null;
  responded_by: string | null;
  responded_at: string | null;
}

/** Документ из комплекта претензии/иска. У претензий нет версионирования,
 *  только догрузка — поэтому привязка идёт сразу к claim_id. */
export interface ClaimDocument {
  id: string;
  claim_id: string;
  filename: string;
  content_type: string | null;
  file_hash: string;
  size_bytes: number;
  ordinal: number;
  created_at: string;
  /** См. ContractVersionFile.content_chars. */
  content_chars?: number;
}

/** Тип уставного документа контрагента. Фиксированный набор для селектора
 *  при загрузке: устав и его редакции, приказы (о назначении директора и т.п.),
 *  доверенности (у них есть срок действия — {@link CounterpartyDocument.valid_until}),
 *  карточка предприятия, документы юр. адреса, всё прочее — `other`.
 *  Значения совпадают с backend-контрактом поля `doc_type`. */
export type CounterpartyDocType =
  | 'charter'
  | 'charter_revision'
  | 'order'
  | 'power_of_attorney'
  | 'company_card'
  | 'legal_address_doc'
  | 'other';

/** Человекочитаемые названия типов для селектора и таблицы. */
export const COUNTERPARTY_DOC_TYPE_LABELS: Record<CounterpartyDocType, string> = {
  charter: 'Устав',
  charter_revision: 'Редакция устава',
  order: 'Приказ',
  power_of_attorney: 'Доверенность',
  company_card: 'Карточка предприятия',
  legal_address_doc: 'Документ юр. адреса',
  other: 'Иное',
};

/** Порядок типов в селекторе загрузки — от самых частых к прочему. */
export const COUNTERPARTY_DOC_TYPE_ORDER: readonly CounterpartyDocType[] = [
  'charter',
  'charter_revision',
  'order',
  'power_of_attorney',
  'company_card',
  'legal_address_doc',
  'other',
];

/** Подпись типа документа с мягким фолбэком: неизвестный/пустой `doc_type`
 *  (legacy-запись, произвольное значение с backend) не должен ронять таблицу —
 *  показываем «Иное». */
export function counterpartyDocTypeLabel(
  t: CounterpartyDocType | string | null | undefined,
): string {
  if (!t) return COUNTERPARTY_DOC_TYPE_LABELS.other;
  // cast нужен для индексации Record известными ключами; неизвестная строка
  // даёт undefined и гасится `?? other` — рантайм-безопасно.
  return COUNTERPARTY_DOC_TYPE_LABELS[t as CounterpartyDocType] ?? COUNTERPARTY_DOC_TYPE_LABELS.other;
}

/** Уставный документ контрагента (устав, приказы, доверенности, карточка
 *  предприятия, документы юр. адреса). Хранится в общем репозитории карточки
 *  контрагента; в отличие от {@link ClaimDocument} несёт `doc_type` и опц.
 *  `valid_until` (актуально для доверенностей). */
export interface CounterpartyDocument {
  id: string;
  counterparty_id: string;
  filename: string;
  content_type?: string | null;
  file_hash: string;
  size_bytes: number;
  ordinal: number;
  /** Тип из фиксированного набора; произвольная строка/`null` трактуется как
   *  «Иное» через {@link counterpartyDocTypeLabel}. */
  doc_type?: CounterpartyDocType | string | null;
  /** Срок действия (YYYY-MM-DD) — заполняется для доверенностей. null — бессрочный
   *  документ (устав и пр.). */
  valid_until?: string | null;
  /** Кто загрузил. Backend может отдать уже разрезолвленный e-mail/имя. */
  uploaded_by?: string | null;
  /** Сколько символов текста извлечено при парсинге. 0 = скан без текстового
   *  слоя. См. {@link ContractVersionFile.content_chars}. Опционально на случай
   *  legacy-записей до подсчёта. */
  content_chars?: number;
  created_at: string;
}

/** Реквизит контрагента, по которому проводится сверка с уставными
 *  документами. Значения совпадают с backend-контрактом поля `field`
 *  в {@link RequisiteDiscrepancy}. Строка-фолбэк — на случай новых полей
 *  с backend без падения UI ({@link requisiteFieldLabel}). */
export type RequisiteField =
  | 'director_name'
  | 'director_position'
  | 'address'
  | 'ogrn'
  | 'inn'
  | 'kpp'
  | 'legal_form';

/** Человекочитаемые названия реквизитов для таблицы сверки. */
export const REQUISITE_FIELD_LABELS: Record<RequisiteField, string> = {
  director_name: 'Директор',
  director_position: 'Должность руководителя',
  address: 'Адрес',
  ogrn: 'ОГРН',
  inn: 'ИНН',
  kpp: 'КПП',
  legal_form: 'Орг.-правовая форма',
};

/** Подпись реквизита с мягким фолбэком: неизвестное/новое поле с backend
 *  не должно ронять таблицу — показываем сырой ключ вместо ошибки. */
export function requisiteFieldLabel(f: RequisiteField | string): string {
  return REQUISITE_FIELD_LABELS[f as RequisiteField] ?? f;
}

/** Тяжесть расхождения реквизита. `warning` — расхождение, требующее
 *  внимания юриста (напр. другой директор в приказе, чем в ЕГРЮЛ);
 *  `info` — незначительное/форматное (напр. запись адреса). Строка-фолбэк
 *  трактуется как `info`. Тон сигнала — информационный, не автоматический
 *  вывод: финальное решение за юристом. */
export type RequisiteSeverity = 'warning' | 'info';

/** Одно расхождение реквизита контрагента с загруженными документами:
 *  значение из источника A против значения из источника B. Источники —
 *  человекочитаемые строки backend (напр. «ЕГРЮЛ», «документ: устав»). */
export interface RequisiteDiscrepancy {
  field: RequisiteField | string;
  value_a: string;
  source_a: string;
  value_b: string;
  source_b: string;
  severity: RequisiteSeverity | string;
  /** id уставного документа-источника расхождения (для перехода к нему в
   *  списке «Документы контрагента»). null — источник не привязан к файлу
   *  (напр. сверка с данными ЕГРЮЛ). */
  doc_id: string | null;
}

/** Полный результат сверки реквизитов по кнопке
 *  (`GET /counterparties/{id}/requisite-check`). */
export interface RequisiteCheck {
  discrepancies: RequisiteDiscrepancy[];
  documents_analyzed: number;
  /** RFC3339 UTC — момент последней сверки. */
  checked_at: string;
}

/** Быстрая отметка расхождений в {@link Counterparty.risk_flags} —
 *  чтобы показать индикатор на карточке без отдельного запроса. Детали
 *  тянутся {@link RequisiteCheck} по кнопке. */
export interface RequisiteChanges {
  has_changes: boolean;
  warning_count: number;
  info_count: number;
  documents_analyzed: number;
  /** RFC3339 UTC. */
  checked_at: string;
}

/** Иск (lawsuit) — отдельный пайплайн ADR-0001 для генерации искового
 *  заявления. Источник претензии: либо ссылка на существующую `Claim`,
 *  либо ручная загрузка DOCX. Аналогично с договором. blob-колонки
 *  бэкенд возвращает только при отдельном GET /manual-*-blob — здесь их нет. */
export type LawsuitStatus = 'draft' | 'composing' | 'ready' | 'failed';

/** Снимок фактуры на момент создания иска. Бэкенд кладёт сюда поля из
 *  исходных claim/contract (см. statut.rs::create_lawsuit), чтобы UI
 *  мог показывать осмысленные названия даже после `ON DELETE SET NULL`
 *  по источникам. */
export interface LawsuitMetadata {
  claim_snapshot?: {
    text?: string;
    counterparty?: string;
    claim_id_at_create?: string;
    direction?: string;
    claim_date?: string;
    claim_number?: string;
    amount?: number;
  };
  contract_snapshot?: {
    counterparty?: string;
    contract_id_at_create?: string;
    subject?: string;
    signed_at?: string;
  };
}

export interface Lawsuit {
  id: string;
  claim_id: string | null;
  contract_id: string | null;
  project_id?: string | null;
  manual_claim_filename: string | null;
  manual_contract_filename: string | null;
  title: string;
  status: LawsuitStatus | string;
  error_message: string | null;
  metadata: LawsuitMetadata;
  created_at: string;
  updated_at: string;
}

/** Метаданные одного сгенерированного DOCX (без blob — файл качается
 *  отдельным GET /composed/{id}/download). Единая форма для всех источников:
 *  список архива (listComposedArchive), история по претензии/договору/иску и
 *  карточка документа (getComposedDoc.item) возвращают ровно эти поля.
 *  Ровно один из claim_id / contract_id / lawsuit_id заполнен (CHECK
 *  constraint миграции 0051). */
export interface ComposedDocMeta {
  id: string;
  claim_id: string | null;
  contract_id: string | null;
  /** Появился в getLawsuit/getComposedDoc; в старых list-эндпоинтах поля
   *  могло не быть — отсюда optional. */
  lawsuit_id?: string | null;
  template_id: string | null;
  size_bytes: number;
  source_similarity: number | null;
  llm_model: string | null;
  content_hash: string;
  created_at: string;
  /** Тип исходного шаблона: 'protocol_disagreement' | 'response_claim' |
   *  'outgoing_claim' | 'lawsuit'. null — шаблон удалён. */
  source_template_kind: string | null;
}

/** Ответ GET /agents/statut/composed/{id} — карточка одного DOCX со
 *  scope'ом (для аудита) и обогащёнными связями (источник). */
export interface ComposedDocDetails {
  item: ComposedDocMeta;
  /** Совпадает с AuditScope минус 'contract'/'claim': конкретный тип
   *  сгенерированного документа. Передаётся в ExternalAuditModal как scope. */
  scope: 'protocol_disagreement' | 'response_claim' | 'outgoing_claim' | 'lawsuit';
  related: {
    contract: { id: string; counterparty: string; number: string | null } | null;
    claim: { id: string; counterparty: string; claim_type: string } | null;
    lawsuit: { id: string; title: string; status: string } | null;
  };
}

/** Подпись для исходной претензии в реестре исков и карточке. Берём
 *  фактуру из `metadata.claim_snapshot` (контрагент + дата) — она пишется
 *  в момент создания иска и переживает удаление исходного claim'а.
 *  Если snapshot пуст (legacy / ручная загрузка) — показываем имя файла
 *  или дефолт. */
export function lawsuitClaimLabel(l: Lawsuit): string {
  const snap = l.metadata.claim_snapshot;
  if (snap?.counterparty) {
    const parts: string[] = [];
    if (snap.claim_number) parts.push(`№ ${snap.claim_number}`);
    if (snap.claim_date) {
      const d = new Date(snap.claim_date);
      if (!Number.isNaN(d.getTime())) {
        parts.push(`от ${d.toLocaleDateString('ru-RU')}`);
      }
    }
    const suffix = parts.length ? ` (${parts.join(' ')})` : '';
    return `${snap.counterparty}${suffix}`;
  }
  if (l.manual_claim_filename) return l.manual_claim_filename;
  return 'Без названия';
}

/** Подпись для договора. Приоритет — subject (предмет договора, обычно
 *  короткое описание) + counterparty; иначе только counterparty; иначе
 *  manual_contract_filename; иначе дефолт. */
export function lawsuitContractLabel(l: Lawsuit): string {
  const snap = l.metadata.contract_snapshot;
  if (snap?.subject || snap?.counterparty) {
    if (snap.subject && snap.counterparty) {
      return `${snap.counterparty} — ${snap.subject}`;
    }
    return snap.subject ?? snap.counterparty ?? '';
  }
  if (l.manual_contract_filename) return l.manual_contract_filename;
  return 'Без названия';
}

/** Контрагент, упомянутый в документе (LLM-extracted ИНН + JOIN на counterparties).
 *  Возвращается с обогащением — фронту не нужен второй запрос для деталей. */
export interface EntityCounterpartyEnriched {
  counterparty_id: string;
  role: string;
  extracted_inn: string | null;
  inn: string;
  full_name: string | null;
  short_name: string | null;
  status: string;
  address: string | null;
}

export interface ContractBriefAttachment {
  id: string;
  filename: string;
  document_kind: string | null;
  size_bytes?: number;
  created_at: string;
}

export interface ContractBriefPenalty {
  id: string;
  category: string;
  severity: string;
  description: string;
  clause_quote?: string | null;
}

/** Безопасный паспорт (сводка) договора для смежных служб (`/contracts/:id/brief`).
 *  Финансовый блок (`sum`, `currency`, платёжные условия) присутствует только если
 *  `can_see_financials === true` (доступ на чтение к `statut` или `econ`). */
export interface ContractBrief {
  contract: {
    id: string;
    contract_number: string | null;
    title: string;
    counterparty: string;
    counterparty_inn?: string | null;
    contract_type: string | null;
    subject: string | null;
    sum: number | null;
    currency: string | null;
    signed_at: string | null;
    effective_from?: string | null;
    expires_at: string | null;
    status: string;
    project_id: string | null;
    can_see_financials: boolean;
  };
  execution_terms: ExecutionTerm[];
  handoffs: import('@entities/notification/types').ContractHandoff[];
  deadlines: Deadline[];
  attachments?: ContractBriefAttachment[];
  penalties?: ContractBriefPenalty[];
}
